(() => {
  'use strict';

  /** @typedef {{ preload: boolean, ttlMinutes: number, rawUrls: string }} CsvConfig */
  /** @typedef {{ text: string, savedAt: number }} CsvEntry */

  const configElement = document.getElementById('SmibCsvCacheConfig');
  const storagePrefix = 'smib-csv-cache:v2:';
  /** @type {Map<string, CsvEntry>} */
  const memoryCache = new Map();
  /** @type {Map<string, Promise<string>>} */
  const pendingRequests = new Map();

  /** @returns {CsvConfig} */
  function readConfig() {
    if (!configElement) {
      return { preload: true, ttlMinutes: 60, rawUrls: '' };
    }

    try {
      const parsed = JSON.parse(configElement.textContent || '{}');

      return {
        preload: parsed.preload !== false,
        ttlMinutes: Math.max(0, Number(parsed.ttlMinutes) || 60),
        rawUrls: typeof parsed.rawUrls === 'string' ? parsed.rawUrls : '',
      };
    } catch (error) {
      console.warn('[SMIB CSV] De centrale instellingen konden niet worden gelezen.', error);
      return { preload: true, ttlMinutes: 60, rawUrls: '' };
    }
  }

  /** @param {unknown} value */
  function normalizeUrl(value) {
    const url = String(value || '').trim();

    if (!url) return '';

    return url.includes('/pubhtml')
      ? url.replace('/pubhtml', '/pub?output=csv')
      : url;
  }

  /** @param {string} rawUrls */
  function parseUrls(rawUrls) {
    const seen = new Set();

    return String(rawUrls || '')
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(line => line && !line.startsWith('#'))
      .map(normalizeUrl)
      .filter(url => {
        if (!url || seen.has(url)) return false;
        seen.add(url);
        return true;
      });
  }

  /** @param {string} url */
  function getStorageKey(url) {
    return `${storagePrefix}${encodeURIComponent(url)}`;
  }

  /**
   * @param {string} url
   * @param {number} maxAge
   * @returns {CsvEntry | null}
   */
  function readStoredEntry(url, maxAge) {
    try {
      const value = localStorage.getItem(getStorageKey(url));
      if (!value) return null;

      /** @type {Partial<CsvEntry> | null} */
      const entry = JSON.parse(value);
      if (
        entry &&
        typeof entry.text === 'string' &&
        typeof entry.savedAt === 'number' &&
        Number.isFinite(entry.savedAt) &&
        Date.now() - entry.savedAt < maxAge
      ) {
        return { text: entry.text, savedAt: entry.savedAt };
      }
      localStorage.removeItem(getStorageKey(url));
    } catch (error) {
      console.warn('[SMIB CSV] Browsercache kon niet worden gelezen.', error);
    }

    return null;
  }

  /**
   * @param {string} url
   * @param {CsvEntry} entry
   */
  function storeEntry(url, entry) {
    memoryCache.set(url, entry);

    try {
      localStorage.setItem(getStorageKey(url), JSON.stringify(entry));
    } catch (error) {
      console.warn('[SMIB CSV] Browsercache kon niet worden opgeslagen.', error);
    }
  }

  const config = readConfig();
  const urls = parseUrls(config.rawUrls);
  const defaultTtlMinutes = Math.max(0, Number(config.ttlMinutes) || 60);

  /** @type {SmibCsvCacheApi} */
  const api = {
    urls: Object.freeze([...urls]),
    ready: Promise.resolve([]),

    getUrl(sourceIndex = 1) {
      const index = Math.max(1, Number(sourceIndex) || 1);
      return urls[index - 1] || '';
    },

    get(source, ttlMinutes = defaultTtlMinutes) {
      const url =
        typeof source === 'number' || /^\d+$/.test(String(source || ''))
          ? this.getUrl(Number(source))
          : normalizeUrl(source);

      if (!url) {
        return Promise.reject(new Error('Geen centrale CSV-bron ingesteld.'));
      }

      const maxAge =
        Math.max(0, Number(ttlMinutes) || defaultTtlMinutes) * 60 * 1000;
      const memoryEntry = memoryCache.get(url);

      if (memoryEntry && Date.now() - memoryEntry.savedAt < maxAge) {
        return Promise.resolve(memoryEntry.text);
      }

      const storedEntry = readStoredEntry(url, maxAge);
      if (storedEntry) {
        memoryCache.set(url, storedEntry);
        return Promise.resolve(storedEntry.text);
      }

      const pendingRequest = pendingRequests.get(url);
      if (pendingRequest) {
        return pendingRequest;
      }

      const request = fetch(url, { cache: 'force-cache' })
        .then(response => {
          if (!response.ok) {
            throw new Error(`CSV kon niet worden geladen (${response.status}).`);
          }

          return response.text();
        })
        .then(text => {
          storeEntry(url, { text, savedAt: Date.now() });
          return text;
        })
        .finally(() => {
          pendingRequests.delete(url);
        });

      pendingRequests.set(url, request);
      return request;
    },

    preload() {
      return Promise.allSettled(urls.map(url => this.get(url)));
    },

    clear(source) {
      const url =
        typeof source === 'number' || /^\d+$/.test(String(source || ''))
          ? this.getUrl(Number(source))
          : normalizeUrl(source);

      if (!url) return;

      memoryCache.delete(url);
      pendingRequests.delete(url);

      try {
        localStorage.removeItem(getStorageKey(url));
      } catch (error) {
        console.warn('[SMIB CSV] Browsercache kon niet worden verwijderd.', error);
      }
    },

    clearAll() {
      urls.forEach(url => this.clear(url));
    }
  };

  window.SMIBCsvCache = api;

  api.ready =
    config.preload === false
      ? Promise.resolve([])
      : api.preload().then(results => {
          document.dispatchEvent(
            new CustomEvent('smib:csv-cache-ready', { detail: { results } })
          );
          return results;
        });
})();
